import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import { useSnackbar } from 'notistack';
import { KeenIcon } from '@/components';
import { Container } from '@/components/container';
import {
  useGetAllGeoJsonQuery,
  useManualAnnotationAddMutation,
} from '@/services/RoutingManagementService';
import { useMapboxAccessToken } from '@/hooks/useMapboxAccessToken';
import NumberInput from '@/components/inputControls/NumberInput';

// No props currently required for this page component

const SNAP_MIN_ZOOM = 0;
const SNAP_QUERY_RADIUS_PX = 40;
const SNAP_DEDUP_PRECISION = 7;

type LngLatTuple = [number, number];

type SnapKind = 'vertex' | 'segment';

type QueryableLineLayerId = 'routes-fairways-line' | 'routes-api-line';

type SnapCandidateVertex = {
  kind: 'vertex';
  coordinate: LngLatTuple;
  metersToPointer: number;
};

type SnapCandidateSegment = {
  kind: 'segment';
  coordinate: LngLatTuple; // projected point on segment
  metersToPointer: number;
  segment: { a: LngLatTuple; b: LngLatTuple };
};

type SnapResult = {
  kind: SnapKind;
  coordinate: LngLatTuple;
  metersToPointer: number;
  segment?: { a: LngLatTuple; b: LngLatTuple };
};

const earthCircumferenceMeters = 40075016.686;

const normalizeLngLatKey = (pt: LngLatTuple) =>
  `${pt[0].toFixed(SNAP_DEDUP_PRECISION)},${pt[1].toFixed(
    SNAP_DEDUP_PRECISION
  )}`;

const metersPerPixelAtLat = (latitude: number, zoom: number) => {
  // WebMercator approximation used by Mapbox for scale.
  const latRad = (latitude * Math.PI) / 180;
  return (earthCircumferenceMeters * Math.cos(latRad)) / Math.pow(2, zoom + 8);
};

const isLineGeometry = (
  geometry: GeoJSON.Geometry
): geometry is GeoJSON.LineString | GeoJSON.MultiLineString =>
  geometry.type === 'LineString' || geometry.type === 'MultiLineString';

const extractLineStrings = (geometry: GeoJSON.Geometry): LngLatTuple[][] => {
  if (geometry.type === 'LineString') {
    return [
      (geometry.coordinates as LngLatTuple[]).filter(
        (c): c is LngLatTuple =>
          Array.isArray(c) &&
          c.length >= 2 &&
          typeof c[0] === 'number' &&
          typeof c[1] === 'number'
      ),
    ];
  }

  if (geometry.type === 'MultiLineString') {
    return (geometry.coordinates as LngLatTuple[][]).map((line) =>
      line.filter(
        (c): c is LngLatTuple =>
          Array.isArray(c) &&
          c.length >= 2 &&
          typeof c[0] === 'number' &&
          typeof c[1] === 'number'
      )
    );
  }

  if (geometry.type === 'GeometryCollection') {
    const out: LngLatTuple[][] = [];
    for (const g of geometry.geometries) {
      if (!g) continue;
      if (g.type === 'GeometryCollection') {
        out.push(...extractLineStrings(g));
      } else if (isLineGeometry(g)) {
        out.push(...extractLineStrings(g));
      }
    }
    return out;
  }

  return [];
};

const mercatorMeters = (lngLat: mapboxgl.LngLatLike) => {
  const mc = mapboxgl.MercatorCoordinate.fromLngLat(lngLat);
  return {
    x: mc.x * earthCircumferenceMeters,
    y: mc.y * earthCircumferenceMeters,
  };
};

const projectPointToSegment = (
  p: { x: number; y: number },
  a: { x: number; y: number },
  b: { x: number; y: number }
) => {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const apx = p.x - a.x;
  const apy = p.y - a.y;
  const abLenSq = abx * abx + aby * aby;
  if (abLenSq === 0) {
    return { x: a.x, y: a.y, t: 0 };
  }
  const t = Math.max(0, Math.min(1, (apx * abx + apy * aby) / abLenSq));
  return { x: a.x + t * abx, y: a.y + t * aby, t };
};

const distanceMeters = (a: LngLatTuple, b: LngLatTuple) =>
  new mapboxgl.LngLat(a[0], a[1]).distanceTo(new mapboxgl.LngLat(b[0], b[1]));

const getSnapResult = (params: {
  map: mapboxgl.Map;
  point: mapboxgl.PointLike;
  pointerLngLat: mapboxgl.LngLat;
  queryRadiusPx: number;
  minZoom: number;
  isCtrlPressed: boolean;
  layers: QueryableLineLayerId[];
}): SnapResult | null => {
  const {
    map,
    point,
    pointerLngLat,
    queryRadiusPx,
    minZoom,
    isCtrlPressed,
    layers,
  } = params;

  if (!isCtrlPressed) {
    console.debug('[getSnapResult] early return: Alt not pressed');
    return null;
  }
  if (map.getZoom() < minZoom) {
    console.debug(
      '[getSnapResult] early return: zoom too low',
      map.getZoom(),
      minZoom
    );
    return null;
  }

  const p = point as mapboxgl.Point;
  const box: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [p.x - queryRadiusPx, p.y - queryRadiusPx],
    [p.x + queryRadiusPx, p.y + queryRadiusPx],
  ];

  const existingLayers = layers.filter((layerId) =>
    Boolean(map.getLayer(layerId))
  );
  if (existingLayers.length === 0) {
    console.debug('[getSnapResult] early return: no layers exist', layers);
    return null;
  }

  // Force fairway layers to be visible during snapping regardless of zoom
  existingLayers.forEach((layerId) => {
    const originalMinzoom = map.getLayer(layerId)?.minzoom;
    // Temporarily make layer visible and remove minzoom
    map.setLayoutProperty(layerId, 'visibility', 'visible');
    if (originalMinzoom !== undefined) {
      map.setLayerZoomRange(layerId, 0, 24);
    }
  });

  const rendered = map.queryRenderedFeatures(box, {
    layers: existingLayers,
  });
  if (rendered.length === 0) {
    // Fallback: query all rendered features (no layer filter) to see if any fairway geometry exists at all
    const allRendered = map.queryRenderedFeatures(box);
    console.debug('[getSnapResult] early return: no rendered features', {
      box,
      existingLayers,
      allRenderedCount: allRendered.length,
      allRenderedLayerIds: [
        ...new Set(allRendered.map((f) => f.layer?.id).filter(Boolean)),
      ],
    });
    return null;
  }

  const toleranceMeters =
    queryRadiusPx * metersPerPixelAtLat(pointerLngLat.lat, map.getZoom());

  console.debug('[getSnapResult] candidates', {
    renderedCount: rendered.length,
    toleranceMeters,
  });
  const pointerTuple: LngLatTuple = [pointerLngLat.lng, pointerLngLat.lat];
  const pointerMerc = mercatorMeters(pointerLngLat);

  const seenVertices = new Set<string>();
  let bestVertex: SnapCandidateVertex | null = null;
  let bestSegment: SnapCandidateSegment | null = null;

  for (const f of rendered) {
    const geometry = f.geometry as GeoJSON.Geometry | undefined;
    if (!geometry) continue;

    const lines = extractLineStrings(geometry);
    for (const line of lines) {
      if (line.length < 2) continue;

      // Vertex candidates (highest priority)
      for (const v of line) {
        const key = normalizeLngLatKey(v);
        if (seenVertices.has(key)) continue;
        seenVertices.add(key);

        const m = distanceMeters(pointerTuple, v);
        if (m > toleranceMeters) continue;

        if (!bestVertex || m < bestVertex.metersToPointer) {
          bestVertex = { kind: 'vertex', coordinate: v, metersToPointer: m };
        }
      }

      // Segment candidates (fallback)
      for (let i = 0; i < line.length - 1; i += 1) {
        const a = line[i];
        const b = line[i + 1];
        if (!a || !b) continue;

        const aMerc = mercatorMeters({ lng: a[0], lat: a[1] });
        const bMerc = mercatorMeters({ lng: b[0], lat: b[1] });
        const proj = projectPointToSegment(pointerMerc, aMerc, bMerc);

        const dx = pointerMerc.x - proj.x;
        const dy = pointerMerc.y - proj.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > toleranceMeters) continue;

        // Convert projected mercator meters back to lng/lat.
        const mc = new mapboxgl.MercatorCoordinate(
          proj.x / earthCircumferenceMeters,
          proj.y / earthCircumferenceMeters,
          0
        );
        const ll = mc.toLngLat();
        const snapped: LngLatTuple = [ll.lng, ll.lat];

        if (!bestSegment || dist < bestSegment.metersToPointer) {
          bestSegment = {
            kind: 'segment',
            coordinate: snapped,
            metersToPointer: dist,
            segment: { a, b },
          };
        }
      }
    }
  }

  if (bestVertex) {
    return {
      kind: 'vertex',
      coordinate: bestVertex.coordinate,
      metersToPointer: bestVertex.metersToPointer,
    };
  }

  if (bestSegment) {
    return {
      kind: 'segment',
      coordinate: bestSegment.coordinate,
      metersToPointer: bestSegment.metersToPointer,
      segment: bestSegment.segment,
    };
  }

  return null;
};

const ManualRouteModal: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [fairwaysGeoJson, setFairwaysGeoJson] =
    useState<GeoJSON.FeatureCollection | null>(null);
  const [combinedGeoJson, setCombinedGeoJson] =
    useState<GeoJSON.FeatureCollection | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isInitializingMap, setIsInitializingMap] = useState(false);
  const drawnCoordsRef = useRef<[number, number][]>([]);
  const isDrawingActiveRef = useRef(true);
  const isCtrlDownRef = useRef(false);
  const lastSnapPreviewRef = useRef<SnapResult | null>(null);
  const [drawnGeoJson, setDrawnGeoJson] =
    useState<GeoJSON.FeatureCollection | null>(null);
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [depth, setDepth] = useState<string>('');
  const [pointCount, setPointCount] = useState<number>(0);
  const [webglError, setWebglError] = useState(false);

  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState<string>('');
  const [tagsError, setTagsError] = useState<string>('');

  const { data: allGeoJson, isLoading: isAllGeoJsonLoading } =
    useGetAllGeoJsonQuery(undefined);

  const [manualAnnotationAdd, { isLoading: isSubmitting }] =
    useManualAnnotationAddMutation();

  const { isMapboxReady } = useMapboxAccessToken();

  useEffect(() => {
    let cancelled = false;

    const loadFairways = async () => {
      try {
        setLoadError(null);
        const response = await fetch('/data/fairways.geojson');
        if (!response.ok) {
          throw new Error(
            `Failed to load fairways.geojson: ${response.status}`
          );
        }
        const data = await response.json();

        let featureCollection: GeoJSON.FeatureCollection;

        if (
          data &&
          data.type === 'FeatureCollection' &&
          Array.isArray(data.features)
        ) {
          featureCollection = data as GeoJSON.FeatureCollection;
        } else if (Array.isArray(data)) {
          featureCollection = {
            type: 'FeatureCollection',
            features: data as GeoJSON.Feature[],
          };
        } else {
          throw new Error('Unexpected fairways.geojson format');
        }

        if (!cancelled) {
          setFairwaysGeoJson(featureCollection);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError((error as Error).message);
        }
      }
    };

    loadFairways();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!fairwaysGeoJson && !allGeoJson) {
      return;
    }

    const features: GeoJSON.Feature[] = [
      ...(fairwaysGeoJson?.features ?? []),
      ...(allGeoJson?.features ?? []),
    ];

    setCombinedGeoJson({
      type: 'FeatureCollection',
      features,
    });
  }, [fairwaysGeoJson, allGeoJson]);

  useEffect(() => {
    if (!combinedGeoJson || !mapContainerRef.current || !isMapboxReady) {
      return;
    }

    setIsInitializingMap(true);
    drawnCoordsRef.current = [];
    isDrawingActiveRef.current = true;
    isCtrlDownRef.current = false;
    setDrawnGeoJson(null);
    setPointCount(0);

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    let center: [number, number] = [24.9384, 60.1699];

    if (combinedGeoJson.features.length > 0) {
      const firstGeometry = combinedGeoJson.features[0].geometry;

      const getFirstPosition = (coord: unknown): [number, number] | null => {
        if (Array.isArray(coord) && coord.length > 0) {
          if (typeof coord[0] === 'number' && coord.length >= 2) {
            return [coord[0] as number, coord[1] as number];
          }
          if (Array.isArray(coord[0])) {
            return getFirstPosition(coord[0]);
          }
        }
        return null;
      };

      if (firstGeometry && firstGeometry.type !== 'GeometryCollection') {
        const maybeCoords = (
          firstGeometry as
            | GeoJSON.Point
            | GeoJSON.LineString
            | GeoJSON.Polygon
            | GeoJSON.MultiPoint
            | GeoJSON.MultiLineString
            | GeoJSON.MultiPolygon
        ).coordinates;
        const firstPos = getFirstPosition(maybeCoords);
        if (firstPos) {
          center = firstPos;
        }
      }
    }

    if (!mapboxgl.supported()) {
      setWebglError(true);
      setIsInitializingMap(false);
      return;
    }

    let map: mapboxgl.Map;
    try {
      map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/hmesikam/cmhkf5ut7001v01sgdjrdhn2b',
        center,
        zoom: 7,
      });

      mapRef.current = map;
    } catch (e) {
      console.error('WebGL Error:', e);
      setWebglError(true);
      setIsInitializingMap(false);
      return;
    }
    map.doubleClickZoom.disable();

    const cleanupFns: Array<() => void> = [];

    map.on('load', () => {
      if (!mapRef.current) return;

      // Fairways from static file as black lines
      if (mapRef.current.getSource('routes-fairways')) {
        mapRef.current.removeLayer('routes-fairways-line');
        mapRef.current.removeSource('routes-fairways');
      }

      if (fairwaysGeoJson) {
        mapRef.current.addSource('routes-fairways', {
          type: 'geojson',
          data: fairwaysGeoJson,
        });

        mapRef.current.addLayer({
          id: 'routes-fairways-line',
          type: 'line',
          source: 'routes-fairways',
          paint: {
            'line-color': '#000000',
            'line-width': 1.5,
            'line-opacity': 0.7,
          },
        });
      }

      // API geojson/all as green lines
      if (mapRef.current.getSource('routes-api')) {
        mapRef.current.removeLayer('routes-api-line');
        mapRef.current.removeSource('routes-api');
      }

      if (allGeoJson) {
        mapRef.current.addSource('routes-api', {
          type: 'geojson',
          data: allGeoJson,
        });

        mapRef.current.addLayer({
          id: 'routes-api-line',
          type: 'line',
          source: 'routes-api',
          paint: {
            'line-color': '#00AA00',
            'line-width': 1.5,
            'line-opacity': 0.8,
          },
        });
      }

      const emptyCollection: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [],
      };

      mapRef.current.addSource('manual-route', {
        type: 'geojson',
        data: emptyCollection,
      });

      mapRef.current.addLayer({
        id: 'manual-route-line',
        type: 'line',
        source: 'manual-route',
        paint: {
          'line-color': '#ff0000',
          'line-width': 2,
          'line-opacity': 0.9,
        },
      });

      mapRef.current.addLayer({
        id: 'manual-route-points',
        type: 'circle',
        source: 'manual-route',
        paint: {
          'circle-radius': 4,
          'circle-color': '#ff0000',
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff',
        },
      });

      const emptySnapPreview: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features: [],
      };

      if (mapRef.current.getSource('snap-preview')) {
        if (mapRef.current.getLayer('snap-preview-segment')) {
          mapRef.current.removeLayer('snap-preview-segment');
        }
        if (mapRef.current.getLayer('snap-preview-point')) {
          mapRef.current.removeLayer('snap-preview-point');
        }
        mapRef.current.removeSource('snap-preview');
      }

      mapRef.current.addSource('snap-preview', {
        type: 'geojson',
        data: emptySnapPreview,
      });

      mapRef.current.addLayer({
        id: 'snap-preview-segment',
        type: 'line',
        source: 'snap-preview',
        filter: ['==', ['geometry-type'], 'LineString'],
        paint: {
          'line-color': '#2563eb',
          'line-width': 4,
          'line-opacity': 0.9,
        },
        layout: {
          visibility: 'none',
        },
      });

      mapRef.current.addLayer({
        id: 'snap-preview-point',
        type: 'circle',
        source: 'snap-preview',
        filter: ['==', ['geometry-type'], 'Point'],
        paint: {
          'circle-radius': 7,
          'circle-color': '#2563eb',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': 0.95,
        },
        layout: {
          visibility: 'none',
        },
      });

      const updateManualRouteSource = () => {
        if (!mapRef.current) return;
        const source = mapRef.current.getSource(
          'manual-route'
        ) as mapboxgl.GeoJSONSource;
        if (!source) return;

        if (drawnCoordsRef.current.length < 2) {
          source.setData(emptyCollection);
          return;
        }

        const feature: GeoJSON.Feature<GeoJSON.LineString> = {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: drawnCoordsRef.current,
          },
        };

        const collection: GeoJSON.FeatureCollection = {
          type: 'FeatureCollection',
          features: [feature],
        };

        source.setData(collection);
        setDrawnGeoJson(collection);
      };

      const setSnapPreviewVisibility = (shouldShow: boolean) => {
        if (!mapRef.current) return;
        const visibility = shouldShow ? 'visible' : 'none';
        if (mapRef.current.getLayer('snap-preview-segment')) {
          mapRef.current.setLayoutProperty(
            'snap-preview-segment',
            'visibility',
            visibility
          );
        }
        if (mapRef.current.getLayer('snap-preview-point')) {
          mapRef.current.setLayoutProperty(
            'snap-preview-point',
            'visibility',
            visibility
          );
        }
      };

      const clearSnapPreview = () => {
        lastSnapPreviewRef.current = null;
        if (!mapRef.current) return;
        const source = mapRef.current.getSource(
          'snap-preview'
        ) as mapboxgl.GeoJSONSource;
        source?.setData({ type: 'FeatureCollection', features: [] });
      };

      const updateSnapPreview = (snap: SnapResult | null) => {
        lastSnapPreviewRef.current = snap;
        if (!mapRef.current) return;
        const source = mapRef.current.getSource(
          'snap-preview'
        ) as mapboxgl.GeoJSONSource;
        if (!source) return;

        if (!snap) {
          source.setData({ type: 'FeatureCollection', features: [] });
          return;
        }

        const features: GeoJSON.Feature[] = [
          {
            type: 'Feature',
            properties: { kind: snap.kind },
            geometry: {
              type: 'Point',
              coordinates: snap.coordinate,
            },
          },
        ];

        if (snap.kind === 'segment' && snap.segment) {
          features.push({
            type: 'Feature',
            properties: { kind: 'segment' },
            geometry: {
              type: 'LineString',
              coordinates: [snap.segment.a, snap.segment.b],
            },
          });
        }

        source.setData({
          type: 'FeatureCollection',
          features,
        });
      };

      const canSnapNow = () => {
        if (!mapRef.current) return false;
        if (!isDrawingActiveRef.current) return false;
        if (!isCtrlDownRef.current) return false;
        return mapRef.current.getZoom() >= SNAP_MIN_ZOOM;
      };

      const syncSnapUi = () => {
        const shouldShow = canSnapNow();
        setSnapPreviewVisibility(shouldShow);
        if (!shouldShow) {
          clearSnapPreview();
        }
      };

      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Alt') return;
        if (isCtrlDownRef.current) return;
        isCtrlDownRef.current = true;
        syncSnapUi();
      };

      const handleKeyUp = (e: KeyboardEvent) => {
        if (e.key !== 'Alt') return;
        if (!isCtrlDownRef.current) return;
        isCtrlDownRef.current = false;
        syncSnapUi();
      };

      const handleWindowBlur = () => {
        if (!isCtrlDownRef.current) return;
        isCtrlDownRef.current = false;
        syncSnapUi();
      };

      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('keyup', handleKeyUp);
      window.addEventListener('blur', handleWindowBlur);

      cleanupFns.push(() => {
        window.removeEventListener('keydown', handleKeyDown);
        window.removeEventListener('keyup', handleKeyUp);
        window.removeEventListener('blur', handleWindowBlur);
      });

      mapRef.current.on('zoom', syncSnapUi);
      cleanupFns.push(() => {
        mapRef.current?.off('zoom', syncSnapUi);
      });

      const handleMouseMove = (event: mapboxgl.MapMouseEvent) => {
        if (!mapRef.current) return;
        const canvas = mapRef.current.getCanvas();
        if (!canvas) return;

        if (!canSnapNow()) {
          canvas.style.cursor = '';
          clearSnapPreview();
          return;
        }

        const snap = getSnapResult({
          map: mapRef.current,
          point: event.point,
          pointerLngLat: event.lngLat,
          queryRadiusPx: SNAP_QUERY_RADIUS_PX,
          minZoom: SNAP_MIN_ZOOM,
          isCtrlPressed: isCtrlDownRef.current,
          layers: ['routes-fairways-line', 'routes-api-line'],
        });

        updateSnapPreview(snap);
        canvas.style.cursor = snap ? 'pointer' : 'crosshair';
      };

      mapRef.current.on('mousemove', handleMouseMove);
      cleanupFns.push(() => {
        mapRef.current?.off('mousemove', handleMouseMove);
      });

      const handleClick = (event: mapboxgl.MapMouseEvent) => {
        if (!isDrawingActiveRef.current) return;

        const lngLat = event.lngLat;
        const rawPoint: [number, number] = [lngLat.lng, lngLat.lat];

        let pointToUse: [number, number] = rawPoint;

        if (canSnapNow() && mapRef.current) {
          const snap = getSnapResult({
            map: mapRef.current,
            point: event.point,
            pointerLngLat: event.lngLat,
            queryRadiusPx: SNAP_QUERY_RADIUS_PX,
            minZoom: SNAP_MIN_ZOOM,
            isCtrlPressed: isCtrlDownRef.current,
            layers: ['routes-fairways-line', 'routes-api-line'],
          });
          if (snap) {
            pointToUse = snap.coordinate;
          }
        }

        drawnCoordsRef.current = [...drawnCoordsRef.current, pointToUse];
        updateManualRouteSource();
        setPointCount(drawnCoordsRef.current.length);
      };

      const handleDoubleClick = (event: mapboxgl.MapMouseEvent) => {
        event.preventDefault();
        if (drawnCoordsRef.current.length < 2) return;
        isDrawingActiveRef.current = false;
        setSnapPreviewVisibility(false);
        clearSnapPreview();
      };

      mapRef.current.on('click', handleClick);
      mapRef.current.on('dblclick', handleDoubleClick);
      mapRef.current.on('mousedown', syncSnapUi);

      cleanupFns.push(() => {
        mapRef.current?.off('click', handleClick);
        mapRef.current?.off('dblclick', handleDoubleClick);
        mapRef.current?.off('mousedown', syncSnapUi);
      });

      syncSnapUi();

      setIsInitializingMap(false);
    });

    return () => {
      for (const fn of cleanupFns) {
        try {
          fn();
        } catch {
          // no-op
        }
      }

      if (mapRef.current) {
        const canvas = mapRef.current.getCanvas?.();
        if (canvas && 'style' in canvas) {
          (canvas as HTMLCanvasElement).style.cursor = '';
        }
      }
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [
    combinedGeoJson,
    fairwaysGeoJson,
    allGeoJson,
    enqueueSnackbar,
    isMapboxReady,
  ]);

  const handleResetDrawing = () => {
    drawnCoordsRef.current = [];
    isDrawingActiveRef.current = true;
    setDrawnGeoJson(null);
    setPointCount(0);

    if (!mapRef.current) return;
    const source = mapRef.current.getSource(
      'manual-route'
    ) as mapboxgl.GeoJSONSource;
    if (!source) return;
    source.setData({ type: 'FeatureCollection', features: [] });
  };

  const handleUndoLastPoint = () => {
    if (!mapRef.current) return;
    if (drawnCoordsRef.current.length === 0) return;

    drawnCoordsRef.current = drawnCoordsRef.current.slice(
      0,
      drawnCoordsRef.current.length - 1
    );

    const source = mapRef.current.getSource(
      'manual-route'
    ) as mapboxgl.GeoJSONSource;
    if (!source) return;

    if (drawnCoordsRef.current.length < 2) {
      source.setData({ type: 'FeatureCollection', features: [] });
      setDrawnGeoJson(null);
      setPointCount(drawnCoordsRef.current.length);
      return;
    }

    const feature: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: drawnCoordsRef.current,
      },
    };

    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [feature],
    };

    source.setData(collection);
    setDrawnGeoJson(collection);
    setPointCount(drawnCoordsRef.current.length);
  };

  const validateName = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setNameError('Name is required');
      return false;
    }
    setNameError('');
    return true;
  };

  const validateTags = (newTags: string[]) => {
    if (newTags.length > 10) {
      setTagsError('Maximum 10 tags allowed');
      return false;
    }

    if (newTags.some((t) => t.length > 50)) {
      setTagsError('Tag cannot exceed 50 characters');
      return false;
    }

    setTagsError('');
    return true;
  };

  const handleTagAdd = () => {
    const candidate = tagInput.trim();
    if (!candidate) return;
    if (candidate.length > 50) {
      setTagsError('Tag cannot exceed 50 characters');
      return;
    }
    if (tags.includes(candidate)) {
      setTagInput('');
      return;
    }
    const next = [...tags, candidate];
    if (!validateTags(next)) return;
    setTags(next);
    setTagInput('');
  };

  const handleTagRemove = (tagToRemove: string) => {
    const next = tags.filter((t) => t !== tagToRemove);
    setTags(next);
    validateTags(next);
  };

  const handleClearAllTags = () => {
    setTags([]);
    validateTags([]);
  };

  const handleSaveRoute = async () => {
    if (drawnCoordsRef.current.length < 2) return;

    if (!validateName(name)) {
      enqueueSnackbar('Please provide a name', { variant: 'error' });
      return;
    }

    const properties: Record<string, unknown> = {};
    const nameTrimmed = name.trim();
    const descriptionTrimmed = description.trim();
    const heightTrimmed = height.trim();
    const depthTrimmed = depth.trim();

    let heightValue: number | null = null;
    let depthValue: number | null = null;

    if (heightTrimmed) {
      const hv = Number(heightTrimmed);
      if (!Number.isNaN(hv)) {
        properties.height_m = hv;
        heightValue = hv;
      }
    }
    if (depthTrimmed) {
      const dv = Number(depthTrimmed);
      if (!Number.isNaN(dv)) {
        properties.depth_m = dv;
        depthValue = dv;
      }
    }

    const feature: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties,
      geometry: {
        type: 'LineString',
        coordinates: drawnCoordsRef.current,
      },
    };

    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: [feature],
    };

    try {
      const blob = new Blob([JSON.stringify(collection)], {
        type: 'application/geo+json',
      });
      const file = new File([blob], `manual_route_${Date.now()}.geojson`, {
        type: 'application/geo+json',
      });

      const formData = new FormData();
      formData.append('file', file);

      if (nameTrimmed) {
        formData.append('name', nameTrimmed);
      }

      if (descriptionTrimmed) {
        formData.append('description', descriptionTrimmed);
      }

      if (tags.length > 0) {
        formData.append('tags', JSON.stringify(tags));
      }

      if (heightValue !== null) {
        formData.append('height', heightValue.toString());
      }
      if (depthValue !== null) {
        formData.append('depth', depthValue.toString());
      }

      await manualAnnotationAdd(formData).unwrap();
      enqueueSnackbar('Manual route added successfully', {
        variant: 'success',
      });
      navigate('/routing-management', { state: { tab: 'manual' } });
    } catch (e) {
      console.error('Failed to submit manual annotation', e);
      enqueueSnackbar('Failed to add manual route', { variant: 'error' });
    }
  };

  const isLoading =
    isInitializingMap ||
    isAllGeoJsonLoading ||
    (!combinedGeoJson && !loadError);

  const isNameValid = name.trim().length > 0;

  return (
    <Container>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-semibold">Manual Route</h1>
        <div className="flex gap-2">
          <button
            className="btn btn-light"
            type="button"
            onClick={() => navigate('/routing-management')}
            disabled={isLoading || isSubmitting}
          >
            Cancel
          </button>
          {pointCount > 0 && (
            <button
              type="button"
              className="btn btn-light"
              onClick={handleUndoLastPoint}
              disabled={isLoading || isSubmitting}
            >
              Undo last point
            </button>
          )}
          {drawnGeoJson && (
            <button
              type="button"
              className="btn btn-light"
              onClick={handleResetDrawing}
              disabled={isLoading || isSubmitting}
            >
              Reset
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveRoute}
            disabled={
              !drawnGeoJson || !isNameValid || isLoading || isSubmitting
            }
          >
            Save Route
          </button>
        </div>
      </div>
      {loadError && (
        <div className="text-sm text-red-600 mb-4" role="alert">
          {loadError}
        </div>
      )}
      <div className="text-sm text-gray-600 mb-6">
        <p className="mb-2">
          Fairways from the static dataset are shown in black, backend routes in
          green, and your manual route will appear in red on top of them.
        </p>
        <p className="font-medium mb-1">How to draw a manual route</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Pan the map by dragging with the left mouse button.</li>
          <li>
            Zoom in and out using the mouse wheel or the map zoom controls.
          </li>
          <li>Click once on the map to place the first point of your route.</li>
          <li>
            Click additional points to extend the route. Points will snap to
            fairways and backend routes (vertices first, then nearest point on
            the line) while holding Alt.
          </li>
          <li>
            Double-click the last point to finish drawing. You can still
            pan/zoom the map without adding points.
          </li>
          <li>
            Use the Reset button above to clear the current route and start
            again.
          </li>
          <li>
            When you are happy with the route, click Save Route to upload it.
          </li>
        </ul>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label
                htmlFor="manual-route-name"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <input
                id="manual-route-name"
                type="text"
                className={`input input-bordered w-full h-11 rounded-lg border-gray-300 ${nameError ? 'border-red-500' : ''}`}
                value={name}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setName(nextValue);
                  if (nameError) {
                    validateName(nextValue);
                  }
                }}
                onBlur={(e) => {
                  validateName(e.target.value);
                }}
                autoComplete="off"
                placeholder="Required"
                disabled={isLoading}
              />
              {nameError && (
                <p className="text-red-500 text-xs mt-1" role="alert">
                  {nameError}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor="manual-route-description"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Description
              </label>
              <input
                id="manual-route-description"
                type="text"
                className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
                disabled={isLoading}
              />
            </div>
          </div>
          <div className="mb-6">
            <label
              htmlFor="manual-route-tags-input"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <div className="relative flex-1">
                <input
                  id="manual-route-tags-input"
                  type="text"
                  value={tagInput}
                  onChange={(e) => {
                    setTagInput(e.target.value);
                    if (tagsError) {
                      setTagsError('');
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleTagAdd();
                    }
                  }}
                  autoComplete="off"
                  disabled={isLoading}
                  className={`input input-bordered w-full h-11 rounded-lg border-gray-300 pr-20 ${tagsError ? 'border-red-500' : ''}`}
                  placeholder="Add tag"
                />
                {tags.length > 0 && (
                  <button
                    type="button"
                    onClick={handleClearAllTags}
                    disabled={isLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                    title="Clear all"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={handleTagAdd}
                disabled={!tagInput.trim() || isLoading}
                className="btn btn-sm h-11 btn-primary bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-4 py-2"
              >
                Add Tag
              </button>
            </div>
            {tagsError && (
              <p className="text-red-500 text-xs mt-1" role="alert">
                {tagsError}
              </p>
            )}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {tags.map((tag) => (
                  <div
                    key={tag}
                    className="bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-100 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      disabled={isLoading}
                      className="text-blue-800 hover:text-blue-900"
                      aria-label={`Remove ${tag} tag`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label
                htmlFor="manual-route-height"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Height (m)
              </label>
              <NumberInput
                id="manual-route-height"
                step="any"
                className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                value={height}
                onChange={(e) => setHeight(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
                disabled={isLoading}
              />
            </div>
            <div>
              <label
                htmlFor="manual-route-depth"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Depth (m)
              </label>
              <NumberInput
                id="manual-route-depth"
                step="any"
                className="input input-bordered w-full h-11 rounded-lg border-gray-300"
                value={depth}
                onChange={(e) => setDepth(e.target.value)}
                placeholder="Optional"
                autoComplete="off"
                disabled={isLoading}
              />
            </div>
          </div>
          <div>
            {webglError ? (
              <div className="w-full min-h-[500px] flex items-center justify-center bg-gray-100 rounded-lg border border-gray-200 p-4 sm:p-8 shadow-sm">
                <div className="max-w-md text-center">
                  <div className="flex justify-center mb-4">
                    <KeenIcon
                      icon="shield-cross"
                      className="text-4xl text-danger"
                    />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Map Display Not Available
                  </h3>
                  <p className="text-gray-600 mb-6 font-medium">
                    The map cannot be displayed because WebGL (hardware
                    acceleration) is not available in your browser.
                  </p>
                  <div className="bg-gray-200 p-5 rounded-xl border border-gray-200 text-left shadow-sm">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-widest mb-3">
                      How to enable
                    </p>
                    <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                      {`To enable hardware acceleration:

• Chrome/Edge: Go to Settings → System → Enable "Use hardware acceleration when available"
• Firefox: Go to Settings → General → Performance → Uncheck "Use recommended performance settings" → Check "Use hardware acceleration when available"

After making changes, please restart your browser.`}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                ref={mapContainerRef}
                className="w-full h-[500px] md:h-[600px] rounded-lg overflow-hidden border border-gray-200 shadow-md"
                role="application"
                aria-label="Map for drawing manual route"
              />
            )}
          </div>
          {isLoading && (
            <div className="mt-4 pt-2 text-sm text-gray-600 flex items-center gap-2">
              <KeenIcon icon="spinner" className="animate-spin text-lg" />
              <span>Loading map and route data...</span>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default ManualRouteModal;
